import React, { useState, useMemo } from "react";
import { ChevronDown, BookOpen, Lightbulb, Search } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// ⚠️  RÈGLE DE SYNCHRONISATION AUTOMATIQUE
// ═══════════════════════════════════════════════════════════════════════
// CHAQUE nouvelle fonctionnalité ou modification demandée par l'utilisateur
// DOIT être documentée ici AVANT que la tâche soit marquée comme terminée.
//
// COMMENT AJOUTER UNE SECTION :
//   1. Choisir (ou créer) une catégorie dans CATEGORIES (ligne ~570)
//   2. Ajouter un objet { id, category, title, body } dans SECTIONS
//   3. Mettre à jour HELP_LAST_UPDATED avec la date du jour
//
// Cette règle est également documentée dans :
//   - /app/memory/HELP_SYNC_POLICY.md (règle persistante entre forks)
//   - /app/memory/PRD.md (section "Politique de synchronisation Aide")
// ═══════════════════════════════════════════════════════════════════════

const HELP_LAST_UPDATED = "2026-02-20";  // Format : YYYY-MM-DD

const SECTIONS = [
  // ============ 1. WORKFLOW ============
  {
    id: "workflow",
    category: "Démarrage",
    title: "Le workflow d'un événement (les 7 étapes)",
    body: (
      <>
        <p className="mb-2">Chaque événement suit un parcours guidé par le stepper en haut de la page de détail :</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li><strong>Configurer</strong> — créer l'événement (nom, date, lieu, surfaces, durée par défaut).</li>
          <li><strong>Disponibilités</strong> — envoyer un SMS aux officiels pour connaître qui est dispo.</li>
          <li><strong>Bâtir l'horaire</strong> — créer les parties manuellement ou via import Excel.</li>
          <li><strong>Assigner</strong> — placer les officiels sur chaque partie (picker groupé par état, ou via auto-assignation IA).</li>
          <li><strong>Diffuser</strong> — cliquer sur « Assignations terminées ». SMS envoyé aux officiels. AVANT ce clic, ils ne voient rien.</li>
          <li><strong>Confirmations</strong> — attendre que chacun confirme ses parties. Rappel SMS possible.</li>
          <li><strong>Fermer + Paie</strong> — verrouiller l'horaire, calculer la paie, générer la facture PDF.</li>
        </ol>
        <p className="mt-3 text-sm text-[var(--muted)]">Astuce : clique sur n'importe quelle étape du stepper pour l'ouvrir. Le badge « ← Étape actuelle » indique où tu en es.</p>
      </>
    ),
  },
  {
    id: "quick-start",
    category: "Démarrage",
    title: "Démarrage rapide (5 minutes chrono)",
    body: (
      <>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>Va dans <strong>Officiels</strong> et ajoute tes arbitres/marqueurs (nom, tél, email, grades).</li>
          <li>Va dans <strong>Réglages</strong> et vérifie tes tarifs par grade + tes forfaits clients.</li>
          <li>Depuis <strong>Événements → Nouvel événement</strong>, crée ton mandat.</li>
          <li>Suis les étapes 1 → 7 du stepper. Chaque étape a son propre bouton d'action.</li>
        </ol>
        <p className="mt-3 text-sm text-[var(--muted)]">Le stepper progresse automatiquement en fonction de tes actions — pas besoin de « valider » manuellement chaque étape.</p>
      </>
    ),
  },

  // ============ 2. OFFICIELS ============
  {
    id: "officials",
    category: "Officiels",
    title: "Ajouter des officiels et créer leur compte",
    body: (
      <>
        <p className="mb-2">Deux façons d'ajouter un officiel :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Ajout manuel</strong> — page Officiels → bouton « Ajouter ». Saisis prénom, nom, téléphone, email, grades. L'officiel n'a pas encore de compte de connexion.</li>
          <li><strong>Lien d&apos;inscription public</strong> — dans Officiels, copie ton lien unique (valide 90 jours à partir de sa génération). Tes officiels créent eux-mêmes leur compte avec mot de passe. Tu n&apos;as qu&apos;à valider leur grade après. Bouton <strong>« Régénérer le lien »</strong> pour créer un nouveau lien et invalider l&apos;ancien immédiatement.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Tous les officiels apparaissent dans le picker d'assignation, qu'ils aient un compte ou non. Seuls ceux avec un compte reçoivent les SMS de dispos / d'assignations.</p>
      </>
    ),
  },
  {
    id: "official-categories",
    category: "Officiels",
    title: "Où bloquer les catégories pour un arbitre ?",
    body: (
      <>
        <p className="mb-2"><strong>Emplacement</strong> : ouvre un événement → clique le bouton <strong>« Auto-assigner »</strong> → dans le modal, fais défiler jusqu&apos;à la section <strong>« Catégories bloquées par arbitre »</strong>.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Une matrice apparaît : chaque ligne = un arbitre disponible, chaque colonne = une catégorie <em>présente dans l&apos;horaire du tournoi</em>.</li>
          <li>Coche une case pour <strong>bloquer</strong> cette catégorie à cet arbitre <em>uniquement pour ce tournoi</em>.</li>
          <li>La config est <strong>persistante</strong> : elle est sauvegardée sur l&apos;événement dès que tu lances Prévisualiser ou Appliquer. Tu n&apos;as pas à recommencer d&apos;un run à l&apos;autre.</li>
          <li>La liste des catégories s&apos;ajuste automatiquement à chaque nouvel événement (U11, U13, Junior, Sénior… selon ce que tu as saisi dans tes parties).</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Note : l&apos;ancien champ « Catégories autorisées » du profil de l&apos;officiel a été retiré. La nouvelle méthode par tournoi le remplace complètement (plus souple, pas de config globale à maintenir).</p>
      </>
    ),
  },

  // ============ 3. RÉGLAGES ============
  {
    id: "settings-grades",
    category: "Réglages",
    title: "Grades et tarifs (arbitres + marqueurs)",
    body: (
      <>
        <p className="mb-2">Dans <strong>Réglages → Grades</strong>, tu configures 5 grades arbitre et 3 grades marqueur avec leur tarif par partie.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Le tarif est utilisé automatiquement dans le calcul de la paie.</li>
          <li>Tu peux renommer les grades (ex: « Recrue », « Junior », « Senior », « Élite », « Master »).</li>
          <li>Les tarifs peuvent varier entre grades (ex: 22 $ pour Recrue, 45 $ pour Élite).</li>
        </ul>
      </>
    ),
  },
  {
    id: "settings-packages",
    category: "Réglages",
    title: "Forfaits clients (packages de facturation)",
    body: (
      <>
        <p className="mb-2">Un forfait combine plusieurs services facturables au client (arbitrage + marquage + frais). Tu peux définir jusqu'à 4 forfaits dans <strong>Réglages → Forfaits</strong>.</p>
        <p className="mb-2">Chaque partie de ton horaire peut être associée à un forfait spécifique. Le total facturé au client dépend de la somme des forfaits + extras.</p>
        <p className="text-sm text-[var(--muted)]">Astuce : nomme tes forfaits selon le type de match (ex: « Régulier », « Match éliminatoire », « Finale »).</p>
      </>
    ),
  },
  {
    id: "settings-slots",
    category: "Réglages",
    title: "Créneaux de disponibilité (AM / PM / Soirée)",
    body: (
      <>
        <p className="mb-2">Par défaut, les officiels répondent à leurs dispos par 3 créneaux : <strong>AM</strong> (matin), <strong>PM</strong> (après-midi), <strong>SOIRÉE</strong>.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Configure les heures de chaque créneau dans <strong>Réglages → Créneaux</strong> (ex: AM = 07:00→13:00).</li>
          <li>Tu peux aussi <strong>écraser</strong> les créneaux au niveau d'un événement spécifique (ou par date à l'intérieur d'un événement).</li>
          <li>L'officiel est considéré « dispo pour une partie » si le créneau correspondant est marqué OUI.</li>
        </ul>
      </>
    ),
  },

  // ============ 4. DISPONIBILITÉS ============
  {
    id: "availability-basics",
    category: "Disponibilités",
    title: "Comment demander les dispos aux officiels",
    body: (
      <>
        <p className="mb-2">Étape 2 du workflow. Depuis la page de l'événement :</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>Clique « Demander les dispos par SMS ».</li>
          <li>Sélectionne les officiels concernés + les dates de l&apos;événement.</li>
          <li>✨ <strong>Filtre par rôle</strong> (mars 2026) : bascule entre « Tous / Arbitres / Marqueurs » pour cibler la demande. Utile quand tu veux départager les dispos d&apos;un groupe sans spammer l&apos;autre.</li>
          <li>Rédige (ou utilise le message par défaut) et clique Envoyer.</li>
          <li>Chaque officiel reçoit un SMS avec un lien vers son compte. Il coche OUI/NON par créneau (AM/PM/SOIRÉE) pour chaque date.</li>
        </ol>
      </>
    ),
  },
  {
    id: "availability-matrix",
    category: "Disponibilités",
    title: "Consulter et gérer les réponses",
    body: (
      <>
        <p className="mb-2">Onglet <strong>Disponibilités</strong> sur la page de l'événement :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Matrice officiels × dates avec ✓ (OUI), ✗ (NON) ou — (sans réponse).</li>
          <li>Compteur global « X / Y ont répondu ».</li>
          <li>Bouton « Verrouiller les dispos » — empêche les officiels de modifier leurs réponses. À utiliser AVANT d'assigner pour éviter les changements en cours d'assignation.</li>
          <li>✨ Depuis mars 2026 — <strong>modification admin en un clic</strong> : clique une cellule pour cycler ✓ → ✗ → —. Fonctionne même quand l'événement est verrouillé ou déjà en cours. Idem pour la colonne <strong>Héberg.</strong>.</li>
        </ul>
      </>
    ),
  },
  {
    id: "availability-lodging",
    category: "Disponibilités",
    title: "Question hébergement dans le formulaire de dispos",
    body: (
      <>
        <p className="mb-2">Depuis mars 2026, quand un arbitre remplit ses disponibilités pour un événement, il peut cocher :</p>
        <p className="mb-2 pl-4 font-semibold">« J&apos;ai besoin d&apos;un hébergement pour cet événement »</p>
        <p className="mb-2">Côté admin :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>La matrice des disponibilités affiche une nouvelle colonne <strong>« Héberg. »</strong> — Oui / — pour chaque arbitre.</li>
          <li>Utile pour organiser à l&apos;avance les nuitées lors d&apos;un tournoi hors ville.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">La donnée est stockée dans le doc availability et retournée par <code className="bg-[var(--surface-3)] px-1 rounded">GET /events/{"{id}"}/availabilities</code> (champ <code className="bg-[var(--surface-3)] px-1 rounded">needs_lodging</code>).</p>
      </>
    ),
  },
  {
    id: "availability-hide-unavailable",
    category: "Disponibilités",
    title: "Événements masqués aux arbitres indisponibles",
    body: (
      <>
        <p className="mb-2">Si un officiel répond <strong>NON</strong> à <em>tous</em> les créneaux d'un événement, celui-ci est automatiquement masqué de son tableau de bord (demandes ET parties).</p>
        <p className="mb-2">Objectif : ne pas polluer le dashboard des arbitres avec des événements qu'ils ne feront pas.</p>
        <p className="text-sm text-[var(--muted)]">Toi (admin) tu vois toujours toutes les réponses dans la matrice, y compris les NON complets.</p>
      </>
    ),
  },

  // ============ 5. HORAIRE ============
  {
    id: "excel-import",
    category: "Horaire",
    title: "Importer un horaire depuis Excel ou CSV",
    body: (
      <>
        <p className="mb-2">Étape 3, bouton « Import Excel ». Wizard en 4 étapes :</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li><strong>Upload</strong> — fichier .xlsx, .xls ou .csv. La 1re ligne = en-têtes.</li>
          <li><strong>Mapping</strong> — associe chaque colonne à un champ : Date, Heure, Surface, Catégorie, Équipes, Durée. L'auto-détection reconnaît les mots-clés courants.</li>
          <li><strong>Aperçu</strong> — vérifie les premières lignes. Alerte si plusieurs jours détectés. Fallback vers la durée par défaut si colonne absente.</li>
          <li><strong>Ajouter / Remplacer / Synchroniser</strong> — voir section suivante.</li>
        </ol>
        <p className="mt-3 text-sm text-[var(--muted)]">Formats de date acceptés : ISO (2026-06-01), JJ/MM/AAAA, JJ-MM-AAAA. Heures : HH:MM 24h.</p>
      </>
    ),
  },
  {
    id: "excel-sync-mode",
    category: "Horaire",
    title: "Ajouter vs Remplacer vs Synchroniser",
    body: (
      <>
        <p className="mb-2">3 modes d'import différents :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Ajouter</strong> — les parties du fichier viennent s'ajouter à ton horaire existant. Aucune assignation n'est touchée.</li>
          <li><strong>Remplacer</strong> — supprime toutes les parties existantes et les remplace par le fichier. Les assignations manuelles sont perdues.</li>
          <li><strong>Synchroniser</strong> — recommandé pour un fichier corrigé : conserve les assignations manuelles sur les parties qui matchent (par heure + surface + date), met à jour les autres champs (équipes, catégorie), ajoute les nouvelles parties, supprime celles absentes du fichier.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Une sauvegarde automatique est créée avant chaque import Remplace ou Synchronise (visible dans le modal Sauvegardes).</p>
      </>
    ),
  },
  {
    id: "schedule-manual",
    category: "Horaire",
    title: "Créer et éditer les parties manuellement",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Bouton « + Partie » ajoute une ligne vide à l'horaire.</li>
          <li>Clique sur n'importe quelle cellule (heure, surface, équipes, catégorie) pour éditer inline.</li>
          <li>« Pause » ajoute un bloc pause (une surface ou toutes) — utile pour les repas ou changements de glace.</li>
          <li>Le glisser-décaler d'une partie décale automatiquement les suivantes sur la même surface (preview avant validation).</li>
        </ul>
      </>
    ),
  },
  {
    id: "schedule-filter-mask",
    category: "Horaire",
    title: "Filtres et masquage des indispos",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Filtre par arbitre</strong> — dropdown restreint aux officiels réellement assignés à l'événement.</li>
          <li><strong>Masquer indispo</strong> — coche cette case pour retirer du picker les officiels qui ont dit NON pour la date de la partie. Réduit le risque d'erreur.</li>
          <li><strong>Tri</strong> — clique sur les en-têtes de colonnes (Heure, Surface, Catégorie…) pour trier.</li>
        </ul>
      </>
    ),
  },

  // ============ 6. ASSIGNATION ============
  {
    id: "assign-manual",
    category: "Assignation",
    title: "Assigner manuellement (picker groupé)",
    body: (
      <>
        <p className="mb-2">Clique sur un champ « Arbitre 1 », « Arbitre 2 » ou « Marqueur » d'une partie. Le picker affiche les officiels groupés par état :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>✓ <strong>Dispos</strong> — a répondu OUI pour ce créneau</li>
          <li>? <strong>Sans réponse</strong> — pas encore répondu à la demande</li>
          <li>✗ <strong>Indispo</strong> — a répondu NON (barré, tu peux forcer mais avec avertissement)</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Les officiels dont la catégorie n'est pas autorisée pour ce match sont également grisés.</p>
      </>
    ),
  },
  {
    id: "team-collaboration",
    category: "Compte & équipe",
    title: "Gérer l'équipe & verrou anti-écrasement",
    body: (
      <>
        <p className="mb-2">Depuis février 2026, tu peux inviter des <strong>co-administrateurs</strong> qui partagent l&apos;accès à TES tournois, officiels, paies et paramètres. Fini les logins partagés qui s&apos;écrasent l&apos;un l&apos;autre.</p>
        <p className="mb-2"><strong>1. Inviter un co-admin</strong></p>
        <ul className="list-disc pl-5 space-y-1.5 mb-3">
          <li>Clique sur ton nom en haut à droite → « <em>Gérer l&apos;équipe</em> ».</li>
          <li>Choisis « Inviter un co-admin » puis renseigne un email + un mot de passe initial (12+ caractères, 3 classes).</li>
          <li>Ton co-équipier se connecte avec ce couple et voit exactement les mêmes tournois que toi.</li>
        </ul>
        <p className="mb-2"><strong>2. Retirer un co-admin</strong> — même écran, bouton corbeille à côté de son nom. Seul l&apos;admin principal (couronne 👑) peut ajouter/retirer.</p>
        <p className="mb-2"><strong>3. Verrou optimiste anti-écrasement</strong></p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Chaque événement porte un numéro de version bumpé à chaque sauvegarde.</li>
          <li>Si deux co-admins éditent la même partie et sauvent quasi-simultanément, le second reçoit un avertissement (« <em>Un autre utilisateur vient de modifier…</em> ») et voit la version rafraîchie AVANT de ré-appliquer ses modifs.</li>
          <li>Aucune perte silencieuse — vous savez toujours qui a modifié quoi en dernier.</li>
        </ul>
      </>
    ),
  },
  {
    id: "auto-assign-block-preference",
    category: "Assignation",
    title: "Contraintes de bloc & restrictions (dures)",
    body: (
      <>
        <p className="mb-2">Depuis février 2026, l&apos;auto-assignation applique 3 contraintes <strong>dures</strong> (jamais violées : plutôt laisser un slot vide que d&apos;enfreindre la règle).</p>
        <p className="mb-2"><strong>1. Bloc min au démarrage de la journée</strong> — dans le modal Auto-assigner, section <em>« Contraintes de bloc »</em> :</p>
        <ul className="list-disc pl-5 space-y-1.5 mb-3">
          <li>Un arbitre qui ne peut pas enchaîner au moins <em>« Bloc min »</em> parties dos-à-dos (dispo, catégorie, slot libre) ne démarre PAS sa journée.</li>
          <li>Le look-ahead vérifie l&apos;enchaînement réel sur la même surface — dispos, blocages catégorie et slots manuels sont pris en compte.</li>
          <li>Les ajouts manuels ne sont jamais touchés (`preserve_manual`).</li>
        </ul>
        <p className="mb-2"><strong>2. Bloc max</strong> — plafond dur : l&apos;algo ne dépassera JAMAIS « Bloc max » parties consécutives sur la même surface. Équivalent à `min(consecutive_max, block_max)`.</p>
        <p className="mb-2"><strong>3. Pause min</strong> — préférence de scoring (soft) pour insérer des pauses d&apos;au moins N parties.</p>
        <p className="mb-2"><strong>4. Surfaces éloignées</strong> (soft) — un arbitre affecté à une surface isolée y reste de préférence toute la journée, évitant les déplacements aller-retour entre sites.</p>
        <p className="mb-2"><strong>5. Restrictions par arbitre × jour</strong> (dur) — nouvelle section <em>« Restrictions par arbitre × jour »</em> :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Choisis un arbitre dans la liste déroulante, puis coche les surfaces autorisées pour chaque jour.</li>
          <li>Un arbitre ne sera JAMAIS assigné à une surface non-cochée pour ce jour-là.</li>
          <li>Aucune case cochée pour un jour = aucune restriction pour ce jour (compat).</li>
          <li>La config est persistée sur l&apos;événement — plus besoin de la retaper à chaque run.</li>
        </ul>
      </>
    ),
  },
  {
    id: "auto-assign-slot-precision",
    category: "Assignation",
    title: "Précision des disponibilités par créneau (AM / PM / Soirée)",
    body: (
      <>
        <p className="mb-2">Depuis mars 2026, l&apos;auto-assignation respecte les indisponibilités <strong>par créneau</strong> (matin/après-midi/soirée), pas seulement par jour.</p>
        <p className="mb-2"><strong>Exemple</strong> : un arbitre dispo le samedi matin mais indisponible en soirée. Un match samedi 19:00 ne lui sera <em>plus jamais</em> attribué — même s&apos;il a dit OUI pour le samedi AM.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Le mapping heure → créneau utilise la config des créneaux de l&apos;événement (ou par défaut : AM 08:00–12:00, PM 12:00–17:00, Soirée 17:00–23:00).</li>
          <li>Fonctionne aussi avec des créneaux personnalisés (Réglages ou par date de l&apos;événement).</li>
          <li>Le rapport d&apos;exclusion indique « Non disponible (déclaré) » avec la date/créneau concerné.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Avant : l&apos;algo se contentait de « au moins un OUI dans la journée » → assignation possible même sur un créneau explicitement refusé. Corrigé.</p>
      </>
    ),
  },
  {
    id: "auto-assign-checks",
    category: "Assignation",
    title: "Liste de vérifications après prévisualisation",
    body: (
      <>
        <p className="mb-2">Après avoir cliqué <strong>Prévisualiser</strong>, une <strong>checklist « Critères vérifiés »</strong> apparaît sous les stats. Elle résume tous les critères pris en compte par l&apos;algorithme :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Disponibilités déclarées</strong> — mode strict ou permissif, nombre d&apos;arbitres avec réponse OUI</li>
          <li><strong>Catégories bloquées</strong> — nombre de blocages configurés</li>
          <li><strong>Duos interdits</strong> — nombre de paires configurées</li>
          <li><strong>Arbitres retirés manuellement</strong> — arbitres écartés par l&apos;admin</li>
          <li><strong>Statut des arbitres</strong> — pool éligible (validés/actifs, hors pending/blocked)</li>
          <li><strong>Max consécutif</strong> — respecté (arbitres volants exemptés)</li>
          <li><strong>Max par jour</strong> — plafond appliqué ou non configuré</li>
          <li><strong>Couverture des slots</strong> — X / Y slots couverts</li>
          <li><strong>Assignations manuelles préservées</strong> — nombre conservé</li>
          <li><strong>Équité (écart-type)</strong> — répartition entre les arbitres</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Icônes : ✓ (respecté) · ⚠ (attention, un critère est à vérifier) · — (non applicable).</p>
      </>
    ),
  },
  {
    id: "auto-assign-exclude",
    category: "Assignation",
    title: "Retirer manuellement un arbitre de l'auto-assignation",
    body: (
      <>
        <p className="mb-2">Même si un arbitre s&apos;est déclaré disponible, tu peux l&apos;<strong>écarter</strong> de l&apos;auto-assignation pour ce tournoi.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Ouvre le modal <strong>Auto-assigner</strong> → section « Retirer des arbitres de l&apos;auto-assignation ».</li>
          <li>Coche l&apos;arbitre concerné. Son nom apparaît barré.</li>
          <li>L&apos;algo l&apos;ignore complètement (aucun match ne lui sera attribué).</li>
          <li>La config est persistée sur l&apos;événement — pas besoin de la re-saisir.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Usage typique : un arbitre s&apos;est mis dispo mais tu ne veux pas le booker cette fois-ci (préférence personnelle, coach en congé, etc.). Tu peux le décocher plus tard pour le remettre dans le pool.</p>
      </>
    ),
  },
  {
    id: "auto-assign-strict-mode",
    category: "Assignation",
    title: "Mode strict des disponibilités (auto-activé)",
    body: (
      <>
        <p className="mb-2">Depuis mars 2026, l&apos;auto-assignation applique <strong>automatiquement un mode strict</strong> quand une demande de dispos a été envoyée pour l&apos;événement :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Une demande envoyée</strong> → seuls les arbitres qui ont <em>explicitement répondu OUI</em> pour la date de la partie sont éligibles. Les non-répondants sont exclus (raison « Non disponible (déclaré) »).</li>
          <li><strong>Aucune demande envoyée</strong> → mode permissif comme avant (tous les arbitres actifs sont candidats).</li>
        </ul>
        <p className="mb-2">Le bandeau de l&apos;aperçu indique le mode courant :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>✓ « Mode strict — seuls les arbitres ayant répondu OUI… » = comportement souhaité.</li>
          <li>⚠ « Aucune demande de dispos envoyée… mode permissif » = attention, tous les arbitres seront considérés.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Auparavant, un arbitre qui n&apos;avait pas répondu était traité comme disponible → tous les arbitres étaient assignés. Ce n&apos;est plus le cas.</p>
      </>
    ),
  },
  {
    id: "auto-assign-troubleshoot",
    category: "Assignation",
    title: "Auto-assignation retourne 0 : pourquoi et comment corriger ?",
    body: (
      <>
        <p className="mb-2">Si l&apos;aperçu montre « 0 assignations » avec des slots non couverts, voici les 6 causes possibles par ordre de probabilité :</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li><strong>Aucun arbitre validé</strong> — les arbitres en état <em>« En attente »</em> (juste inscrits) ou <em>« Bloqué »</em> sont ignorés. Valide-les dans <strong>Officiels</strong>. (Corrigé mars 2026 : les statuts <em>« validé »</em> ET <em>« actif »</em> sont maintenant acceptés — auparavant seul <em>« actif »</em> passait.)</li>
          <li><strong>Aucune disponibilité déclarée OUI</strong> — l&apos;algo ne peut placer que les arbitres qui ont répondu OUI à au moins un créneau. Fais un rappel de dispos ou débloque l&apos;étape.</li>
          <li><strong>Toutes les catégories bloquées</strong> — vérifie la matrice « Catégories bloquées par arbitre » dans le modal. Si tous les arbitres sont cochés pour toutes les catégories, aucune assignation possible.</li>
          <li><strong>Duos interdits trop restrictifs</strong> — si toutes les combinaisons d&apos;arbitres sont dans la liste « Duos à ne pas jumeler », l&apos;algo ne trouve aucune paire valide.</li>
          <li><strong>Max par jour trop bas</strong> — si tu as mis <em>Max/jour = 1</em> et que tu as 6 arbitres pour 20 parties, ça bloque vite. Retire la limite ou monte-la.</li>
          <li><strong>Catégories non renseignées</strong> — si tes parties n&apos;ont pas de <em>catégorie</em> mais qu&apos;un arbitre a l&apos;ancienne restriction <code className="bg-[var(--surface-3)] px-1 rounded">allowed_categories</code>, il sera exclu. (Cet ancien champ n&apos;existe plus depuis mars 2026.)</li>
        </ol>
        <p className="mt-3 text-sm text-[var(--muted)]">Diagnostic : le <strong>rapport d&apos;exclusion</strong> (sous l&apos;aperçu) montre EXACTEMENT pourquoi chaque arbitre a été écarté — Cat, Dispo, Max, Bloqué, Duo. Lis-le pour identifier la contrainte à relâcher.</p>
      </>
    ),
  },
  {
    id: "bulk-scorer",
    category: "Assignation",
    title: "Assigner un marqueur en bloc (surface × journée)",
    body: (
      <>
        <p className="mb-2">Bouton <strong>« Marqueur en bloc »</strong> dans la barre d&apos;actions de l&apos;événement.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Sélectionne <em>date</em>, <em>surface</em>, puis <em>marqueur</em>.</li>
          <li>Clique <strong>Appliquer</strong> : toutes les parties de cette (date, surface) reçoivent ce marqueur.</li>
          <li>Si une partie a déjà un marqueur différent, une confirmation apparaît listant les écrasements avant d&apos;appliquer.</li>
          <li>Laisse <em>« Aucun »</em> comme marqueur pour <strong>détacher</strong> tous les marqueurs de cette (date, surface).</li>
        </ul>
      </>
    ),
  },
  {
    id: "bulk-swap-ref",
    category: "Assignation",
    title: "Remplacer un arbitre absent pour toute une journée",
    body: (
      <>
        <p className="mb-2">Bouton <strong>« Remplacer arbitre »</strong> dans la barre d&apos;actions de l&apos;événement.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Choisis <em>date</em>, <em>arbitre absent</em> (limité à ceux qui figurent ce jour-là), et <em>remplaçant</em>.</li>
          <li>L&apos;algorithme détecte les conflits côté remplaçant : catégorie non autorisée, catégorie bloquée, non disponible, collision horaire, duo interdit.</li>
          <li>Si des conflits sont détectés, le modal les liste et propose <strong>Forcer le remplacement</strong>.</li>
          <li>Le swap remplace toutes les parties du jour, y compris celles déjà confirmées, et invalide les confirmations de l&apos;arbitre sortant sur ces parties.</li>
        </ul>
      </>
    ),
  },
  {
    id: "auto-assign",
    category: "Assignation",
    title: "Auto-assignation IA (constraints solver)",
    body: (
      <>
        <p className="mb-2">Bouton « Auto-assigner » ouvre l'assistant IA. Il remplit `referee1_id` et `referee2_id` de chaque partie en respectant :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Catégories autorisées</strong> — jamais un arbitre hors de sa liste `allowed_categories`.</li>
          <li><strong>Disponibilités déclarées</strong> — jamais un arbitre qui a dit NON pour la date/créneau.</li>
          <li><strong>Conflits horaires</strong> — jamais 2 parties simultanées pour la même personne.</li>
          <li><strong>Max consécutif</strong> — nombre max de parties d'affilée sur la même surface (défaut 3).</li>
          <li><strong>Max par jour</strong> — plafond optionnel de parties/jour par arbitre.</li>
          <li><strong>Équité (fairness)</strong> — répartition équilibrée : le score prend en compte l'écart-type des parties par arbitre.</li>
        </ul>
        <p className="mt-3">Mode <strong>Prévisualiser</strong> avant Appliquer : voir aperçu (assignations, slots totaux, non-couverts, indicateur fairness) et rapport détaillé.</p>
        <p className="mt-3 text-sm text-[var(--muted)]">Une sauvegarde automatique est créée AVANT chaque application.</p>
      </>
    ),
  },
  {
    id: "forbidden-pairs",
    category: "Assignation",
    title: "Duos d'arbitres à ne pas jumeler (paires interdites)",
    body: (
      <>
        <p className="mb-2">Dans le modal d&apos;auto-assignation, section <strong>« Duos d&apos;arbitres à ne pas jumeler »</strong> : tu peux ajouter autant de paires interdites que nécessaire.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Choisis Arbitre A + Arbitre B → clique <strong>Ajouter</strong>.</li>
          <li>L&apos;algorithme ne les mettra jamais ensemble comme <em>Arbitre 1 + Arbitre 2</em> sur la même partie.</li>
          <li>Ils peuvent toujours arbitrer d&apos;autres parties séparément.</li>
          <li>Retire un duo en cliquant sur le <strong>×</strong> à côté.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Utile pour éviter certains conflits interpersonnels ou pour interdire des combinaisons de niveau (ex: 2 recrues ensemble). Le rapport d&apos;exclusion indique la raison « Duo interdit » quand elle est déclenchée.</p>
      </>
    ),
  },
  {
    id: "auto-assign-exclusions",
    category: "Assignation",
    title: "Rapport d'exclusion : pourquoi X n'a pas été assigné",
    body: (
      <>
        <p className="mb-2">Après une prévisualisation d'auto-assignation, la section « Officiels non assignés » liste chaque arbitre écarté avec la raison dominante :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Catégorie non autorisée</strong> — les matchs ne correspondent pas à sa liste `allowed_categories`.</li>
          <li><strong>Non disponible (déclaré)</strong> — a répondu NON aux créneaux des parties concernées.</li>
          <li><strong>Conflit horaire</strong> — déjà assigné à une autre partie qui chevauche.</li>
          <li><strong>Max par jour atteint</strong> — a atteint le plafond configuré.</li>
          <li><strong>Trop de parties de pause</strong> — l&apos;écart entre 2 assignations sur la même surface/date dépasse le <em>Max de pause</em>. Depuis mars 2026, c&apos;est une contrainte <strong>dure</strong> : les rejets restent visibles même si l&apos;arbitre a été assigné ailleurs (colonne « Pause »).</li>
          <li><strong>Max consécutif atteint</strong> — depuis mars 2026, la limite <em>Max consécutif</em> est une contrainte <strong>dure</strong> : un arbitre non-volant ne peut plus enchaîner davantage de parties <em>strictement dos-à-dos</em> sur la même surface (colonne « Consec »). ⚠️ Une pause dans son horaire remet le compteur à 1 : il pourra donc reprendre 2 (ou plus) parties d'affilée après un temps mort. Cette limite ne s'applique <em>pas</em> au total de parties de la journée (voir « Max par jour »).</li>
          <li><strong>Changement de surface interdit en streak</strong> ✨ (mars 2026) — un arbitre non-volant ne peut PAS changer de surface pendant une série de parties dos-à-dos ; le changement n'est autorisé qu'après une pause dans son horaire (colonne « Surface »).</li>
          <li><strong>Duo interdit / Catégorie bloquée</strong> — respect des règles configurées dans l&apos;événement.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Les compteurs à droite indiquent le nombre de refus par cause pour cet arbitre. Utile pour ajuster tes contraintes.</p>
        <p className="mt-3 text-sm">✨ <strong>Correctifs suggérés (mars 2026)</strong> : quand des slots restent impossibles à couvrir, un panneau propose des boutons d&apos;action selon les causes détectées — retirer tous les duos interdits, retirer toutes les catégories bloquées, augmenter Max de pause / consécutif / jour d&apos;un cran. Un clic ajuste le paramètre, puis clique <em>Recalculer</em>.</p>
        <p className="mt-2 text-sm">✨ <strong>Frise horaire par arbitre (mars 2026)</strong> : une grille compacte montre chaque arbitre en ligne × chaque créneau du tournoi en colonne. Cellule <strong>noire</strong> = joué, cellule <strong>hachurée</strong> = pause entre deux assignations, cellule vide = hors plage. Idéal pour repérer les streaks trop longs ou les pauses excessives d&apos;un coup d&apos;œil.</p>
      </>
    ),
  },
  {
    id: "floating-referees",
    category: "Assignation",
    title: "Arbitres volants (peuvent dépasser le max consécutif)",
    body: (
      <>
        <p className="mb-2">Dans le modal d&apos;auto-assignation, tu peux cocher un ou plusieurs arbitres comme <strong>volants</strong> pour cet événement.</p>
        <p className="mb-2">Les volants peuvent enchaîner plus de parties consécutives que le max défini (le max consécutif est une contrainte <strong>dure</strong> pour les autres arbitres depuis mars 2026), pour absorber les pauses des autres arbitres. Ils reçoivent un léger bonus de score pour prendre les créneaux « difficiles ».</p>
        <p className="mb-2"><strong>Filtre automatique</strong> : la liste des volants proposés est restreinte aux arbitres qui ont déclaré <em>au moins un créneau disponible</em> pour l&apos;événement. Si personne n&apos;a répondu (mode permissif), tous les arbitres actifs restent proposés.</p>
        <p className="text-sm text-[var(--muted)]">À utiliser pour tes arbitres seniors qui aiment travailler beaucoup dans une même journée.</p>
      </>
    ),
  },

  // ============ 7. CONFLITS ============
  {
    id: "conflicts",
    category: "Assignation",
    title: "Les 3 types de conflits détectés automatiquement",
    body: (
      <>
        <p className="mb-2">Le système surligne 3 types de conflits dans l'horaire :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Double assignation</strong> — un même officiel assigné à 2 parties simultanées (rayures diagonales).</li>
          <li><strong>Chevauchement de surface</strong> — 2 parties au même endroit en même temps (bordure double trait).</li>
          <li><strong>Partie pendant une pause</strong> — une partie chevauche une pause programmée.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Une bannière en haut de l'horaire compte les conflits totaux. Impossible de « forcer » un conflit — à résoudre manuellement.</p>
      </>
    ),
  },

  // ============ 8. RÉSUMÉ ============
  {
    id: "summary-tab",
    category: "Résumé",
    title: "L'onglet Résumé (vue d'ensemble par arbitre)",
    body: (
      <>
        <p className="mb-2">Un onglet dédié qui montre :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Le nombre de parties de chaque officiel, réparties par date.</li>
          <li>Un bloc <strong>Arbitres non assignés</strong> en bleu clair — les arbitres actifs de ta banque qui n'ont AUCUNE partie sur cet événement (pour éviter de les oublier).</li>
          <li>Un bouton « Copier » qui exporte le résumé en texte pour partage rapide.</li>
        </ul>
      </>
    ),
  },

  // ============ 9. SMS TWILIO ============
  {
    id: "sms-twilio",
    category: "SMS",
    title: "Comment fonctionnent les SMS (Twilio)",
    body: (
      <>
        <p className="mb-2">Gestion Dek envoie 3 types de SMS via Twilio :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Demande de dispos</strong> — étape 2, message custom + lien vers /officiel/login.</li>
          <li><strong>Notification d'assignations</strong> — étape 5, automatique au clic « Assignations terminées ». Inclut le nombre de parties assignées.</li>
          <li><strong>Rappel aux non-confirmés</strong> — étape 6, à toute personne assignée mais pas encore confirmée.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">
          Numéros normalisés en E.164 (+1XXXXXXXXXX). Si Twilio n'est pas configuré (preview), les SMS sont loggés en mode <em>mock</em> (visible en base, jamais réellement envoyés). Coût réel : ~0,01–0,03 $ par SMS.
        </p>
      </>
    ),
  },
  {
    id: "diffusion",
    category: "SMS",
    title: "« Diffuser les assignations » vs « Publier l'horaire »",
    body: (
      <>
        <p className="mb-2">Deux actions distinctes :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Diffuser les assignations</strong> (étape 5) — rend les parties visibles aux officiels + envoie SMS. Sans ce clic, les officiels ne voient rien.</li>
          <li><strong>Publier l'horaire</strong> (étape 7, optionnel) — crée un snapshot pour le PDF client et la vue publique <code className="bg-[var(--surface-3)] px-1 rounded">/horaire/:id</code>.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Re-diffuser après ajout de nouveaux officiels n'envoie un SMS QU'aux nouveaux (anti-spam). Historique des diffusions consultable via le lien « Historique ».</p>
      </>
    ),
  },
  {
    id: "confirmations",
    category: "SMS",
    title: "Pourquoi la confirmation d'assignation est obligatoire",
    body: (
      <>
        <p className="mb-2">Quand un officiel est assigné, il doit explicitement cliquer « Confirmer » dans son tableau de bord. Sans ça, le badge reste à ○ (Non confirmé).</p>
        <p className="mb-2"><strong>Pourquoi ?</strong> Une « disponibilité » signifie que l'officiel peut travailler ce jour-là. Mais accepter une assignation spécifique (heure, lieu, partie) est un engagement contractuel séparé — sans confirmation, tu risques des no-shows.</p>
        <p className="mt-3 text-sm text-[var(--muted)]">Utilise « Rappel SMS aux non-confirmés » (étape 6) pour relancer 48h avant l'événement.</p>
      </>
    ),
  },

  // ============ 10. SAUVEGARDES ============
  {
    id: "snapshots",
    category: "Sauvegardes",
    title: "Sauvegardes automatiques et manuelles",
    body: (
      <>
        <p className="mb-2">Un snapshot capture l&apos;état complet de l&apos;horaire à un instant T. Il est créé automatiquement :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Avant chaque application d&apos;auto-assignation</li>
          <li>Avant chaque import Excel en mode Remplacer ou Synchroniser</li>
          <li>Avant certaines actions destructives (suppression massive, etc.)</li>
        </ul>
        <p className="mb-2">Bouton <strong>Sauvegardes</strong> sur la page de l&apos;événement pour :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Créer un snapshot manuel avec ta propre raison</li>
          <li>Lister tous les snapshots (date + raison + auteur)</li>
          <li>Restaurer un snapshot en un clic (crée un nouveau snapshot avant de restaurer, par sécurité)</li>
          <li>Supprimer un snapshot obsolète</li>
        </ul>
      </>
    ),
  },
  {
    id: "snapshots-download",
    category: "Sauvegardes",
    title: "Télécharger une sauvegarde en JSON (secours local)",
    body: (
      <>
        <p className="mb-2">En plus des snapshots serveur, tu peux exporter des <strong>fichiers JSON à conserver sur ton ordinateur</strong> — utiles si un problème frappe la base de données ou avant une opération risquée comme l&apos;auto-assignation.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Depuis le modal Sauvegardes</strong> : bouton <em>« Télécharger l&apos;horaire actuel »</em> exporte l&apos;état courant. Icône <em>Download</em> à côté de chaque snapshot pour télécharger celui-ci.</li>
          <li><strong>Depuis le modal Auto-assigner</strong> : bloc <em>« Avant de lancer, tu peux télécharger l&apos;horaire actuel »</em> juste sous le titre.</li>
          <li>Le fichier contient toutes les parties + assignations + configuration de l&apos;événement.</li>
          <li>Nom automatique : <code className="bg-[var(--surface-3)] px-1 rounded">avant_auto_assign_&lt;event&gt;_&lt;date&gt;.json</code>.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Astuce : garde une copie mensuelle sur ton drive pour un plan de reprise complet.</p>
      </>
    ),
  },

  // ============ 11. PAIE ============
  {
    id: "payroll",
    category: "Paie",
    title: "Calcul des paies (Base + Extras - Déductions)",
    body: (
      <>
        <p className="mb-2">Pour chaque officiel, la paie d'un événement = <strong>Base + Extras - Déductions</strong>.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Base</strong> — pour chaque partie assignée, on prend le tarif du grade configuré dans Réglages (5 grades arbitre, 3 grades marqueur).</li>
          <li><strong>Extras</strong> — éléments positifs à ajouter manuellement (essence, bonus, etc.) — libellé + montant.</li>
          <li><strong>Déductions</strong> — éléments négatifs (vêtements achetés, etc.).</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Le bouton « Facture (PDF) » génère un PDF imprimable (recto = facture client, verso = feuille de paie).</p>
      </>
    ),
  },
  {
    id: "payroll-track-payments",
    category: "Paie",
    title: "Marquer les paiements versés",
    body: (
      <>
        <p className="mb-2">Dans la page <strong>Paies</strong>, sélectionne un événement fermé. Pour chaque officiel, un bouton « Marqué payé » enregistre la date + toi comme auteur.</p>
        <p className="text-sm text-[var(--muted)]">Un clic supplémentaire annule le marquage. Utile pour ton suivi comptable — n'a aucun impact sur le calcul.</p>
      </>
    ),
  },

  // ============ 12. PARTAGE ============
  {
    id: "share-public",
    category: "Partage",
    title: "Partage public de l'horaire (URL client)",
    body: (
      <>
        <p className="mb-2">Bouton <strong>Partager</strong> sur la page de l'événement :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Génère un lien public avec token unique (aucun mot de passe requis).</li>
          <li>Le lien montre l'horaire figé au dernier « Publier » — pas les modifications en cours.</li>
          <li>Aucune donnée personnelle exposée (uniquement prénom + nom des arbitres).</li>
          <li>Tu peux révoquer le lien à tout moment (invalide immédiatement l'URL).</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Utile pour envoyer au tournoi ou à un client sans les faire créer un compte.</p>
      </>
    ),
  },
  {
    id: "share-staff",
    category: "Partage",
    title: "Partage staff (accès en lecture seule)",
    body: (
      <>
        <p className="mb-2">Bouton <strong>Partager au staff</strong> — génère un lien distinct destiné à ton équipe interne (assistants, dirigeants).</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Affiche l'horaire courant (pas juste le snapshot publié).</li>
          <li>Inclut toutes les informations d'assignation.</li>
          <li>Aucune modification possible — 100% lecture seule.</li>
        </ul>
      </>
    ),
  },

  // ============ 13. ARCHIVAGE ============
  {
    id: "auto-archive",
    category: "Archivage",
    title: "Archivage automatique 3 jours après la fin",
    body: (
      <>
        <p className="mb-2">Les événements terminés depuis plus de <strong>3 jours</strong> sont automatiquement masqués de la liste principale.</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Les données restent en base (pas de suppression).</li>
          <li>Sur la page Événements, coche « Afficher les événements archivés » pour les revoir.</li>
          <li>La page Paies affiche toujours les événements fermés, y compris archivés (pour compta rétrospective).</li>
          <li>Côté officiels : leurs anciennes parties/demandes disparaissent aussi de leur dashboard.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">La règle : <code className="bg-[var(--surface-3)] px-1 rounded">date de la dernière partie + 3 jours &lt; aujourd&apos;hui</code>.</p>
      </>
    ),
  },
  {
    id: "cascade-delete",
    category: "Archivage",
    title: "Suppression d'un événement (cascade côté arbitres)",
    body: (
      <>
        <p className="mb-2">Quand tu supprimes un événement via la corbeille sur la liste ou la page de détail :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>L&apos;événement est retiré de ta liste immédiatement.</li>
          <li>Les <strong>demandes de disponibilités</strong> associées sont supprimées → l&apos;événement disparaît aussi du dashboard des arbitres.</li>
          <li>Les <strong>confirmations d&apos;assignation</strong>, <strong>paiements</strong>, <strong>extras</strong>, <strong>logs de diffusion</strong> et <strong>sauvegardes</strong> sont également nettoyés.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Action <strong>irréversible</strong> : utilise plutôt « Fermer » si tu veux conserver l&apos;historique (paie, facturation).</p>
      </>
    ),
  },

  // ============ 14. OFFICIEL (côté arbitre) ============
  {
    id: "official-day-tabs",
    category: "Officiels",
    title: "Onglets par journée dans « Mes parties » (côté arbitre)",
    body: (
      <>
        <p className="mb-2">Sur le dashboard des officiels, chaque événement multi-jours affiche automatiquement des <strong>onglets par jour</strong> (ex: <em>Samedi 6 juin (4)</em>, <em>Dimanche 7 juin (3)</em>).</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Un seul jour d&apos;événement → pas d&apos;onglet (affichage direct).</li>
          <li>Plusieurs jours → l&apos;arbitre voit un onglet par jour avec le compte de parties.</li>
          <li>Les parties sont triées par heure croissante à l&apos;intérieur de chaque jour.</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Améliore la lisibilité pour les tournois multi-jours (fin de semaine, blitz week-end, ligues).</p>
      </>
    ),
  },

  // ============ 15. SÉCURITÉ ============
  {
    id: "security",
    category: "Sécurité",
    title: "Sécurité, sessions et confidentialité",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Politique mot de passe</strong> — min 12 caractères, au moins 3 classes (maj/min/chiffre/spécial).</li>
          <li><strong>Brute force</strong> — 5 tentatives ratées → verrouillage 15 min (par IP).</li>
          <li><strong>Sessions cookies</strong> — httpOnly + secure + samesite=none (impossible à lire en JS).</li>
          <li><strong>Idle logout</strong> — déconnexion automatique après 30 min d'inactivité.</li>
          <li><strong>Audit log</strong> — toutes les actions admin sont tracées (création, modification, diffusion, SMS envoyés).</li>
          <li><strong>Multi-tenant</strong> — chaque gestionnaire ne voit JAMAIS les officiels ou événements des autres.</li>
          <li><strong>Rate limiting</strong> — protection anti-spam sur les endpoints sensibles (signup, reset password).</li>
        </ul>
        <p className="mt-3 text-sm text-[var(--muted)]">Consulte notre <a href="/privacy" className="underline">Politique de confidentialité</a> pour plus de détails.</p>
      </>
    ),
  },
  {
    id: "password-reset",
    category: "Sécurité",
    title: "Réinitialisation de mot de passe (officiels)",
    body: (
      <>
        <p className="mb-2">Un officiel qui a oublié son mot de passe :</p>
        <ol className="list-decimal pl-5 space-y-1.5">
          <li>Va sur /officiel/login → « Mot de passe oublié »</li>
          <li>Saisit son numéro de téléphone</li>
          <li>Reçoit un SMS avec un lien magique (valide 15 min, à usage unique)</li>
          <li>Clique sur le lien → défini un nouveau mot de passe</li>
        </ol>
        <p className="mt-3 text-sm text-[var(--muted)]">Rate limit : 5 demandes/heure par numéro pour éviter le spam SMS.</p>
      </>
    ),
  },

  // ============ 15. SUPPORT ============
  {
    id: "support",
    category: "Support",
    title: "Besoin d'aide supplémentaire ?",
    body: (
      <>
        <p className="mb-2">Pour toute question ou problème :</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Écris à <a href="mailto:info@lesamouraiduweb.com" className="underline">info@lesamouraiduweb.com</a></li>
          <li>Décris ton problème + joins une capture d'écran si possible</li>
          <li>Indique si tu es sur la version <strong>preview</strong> ou <strong>production</strong> (gestiondek.ca)</li>
        </ul>
      </>
    ),
  },
];

const CATEGORIES = [
  "Démarrage",
  "Officiels",
  "Réglages",
  "Disponibilités",
  "Horaire",
  "Assignation",
  "Résumé",
  "SMS",
  "Sauvegardes",
  "Paie",
  "Partage",
  "Archivage",
  "Sécurité",
  "Support",
];

function AccordionItem({ section, isOpen, onToggle }) {
  return (
    <div className="border-b border-[var(--border)]" data-testid={`help-item-${section.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-center justify-between gap-4 py-4 hover:opacity-70 transition-opacity"
        data-testid={`help-toggle-${section.id}`}
      >
        <span className="font-semibold text-base pr-4">{section.title}</span>
        <ChevronDown
          size={18}
          strokeWidth={2.2}
          className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="text-sm text-[var(--text-2)] leading-relaxed pr-8" data-testid={`help-body-${section.id}`}>
            {section.body}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HelpPage() {
  const [openId, setOpenId] = useState("workflow");
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SECTIONS.filter((s) => {
      if (activeCategory !== "all" && s.category !== activeCategory) return false;
      if (!q) return true;
      return (
        s.title.toLowerCase().includes(q) ||
        (typeof s.body === "string" && s.body.toLowerCase().includes(q)) ||
        s.category.toLowerCase().includes(q)
      );
    });
  }, [query, activeCategory]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of filtered) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category).push(s);
    }
    return map;
  }, [filtered]);

  return (
    <div data-testid="help-page" className="max-w-4xl">
      <div className="mb-8">
        <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <BookOpen size={18} strokeWidth={2.2} />
            <span className="label !mb-0">Centre d'aide</span>
          </div>
          <span
            className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] border border-[var(--border-strong)] px-2 py-1"
            data-testid="help-last-updated"
            title="La page Aide est mise à jour à chaque nouvelle fonctionnalité"
          >
            Mise à jour · {HELP_LAST_UPDATED} · {SECTIONS.length} sections
          </span>
        </div>
        <h1 className="h1-big mb-3">Comment ça marche ?</h1>
        <p className="text-base text-[var(--muted)] leading-relaxed">
          Guide complet des fonctionnalités de Gestion Dek — du démarrage rapide aux subtilités des SMS, de l'auto-assignation IA et des sauvegardes.
        </p>
      </div>

      {/* Search + category filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search size={16} strokeWidth={2.2} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une fonctionnalité (ex: import, SMS, paie, sauvegarde…)"
            className="input-box !pl-10"
            data-testid="help-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveCategory("all")}
            className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
              activeCategory === "all" ? "bg-black text-white border-black" : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
            }`}
            data-testid="help-cat-all"
          >
            Tout
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                activeCategory === c ? "bg-black text-white border-black" : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
              }`}
              data-testid={`help-cat-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Sections grouped by category */}
      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--border-strong)] p-10 text-center">
          <p className="font-serif text-2xl italic mb-1">Aucun résultat</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
            Essaie un autre mot-clé ou réinitialise le filtre catégorie.
          </p>
        </div>
      ) : (
        <div className="card-flat !p-0">
          {Array.from(grouped.entries()).map(([cat, items]) => (
            <div key={cat} className="border-b border-[var(--border)] last:border-b-0">
              <div className="px-6 pt-5 pb-2">
                <div className="label !mb-0" data-testid={`help-group-${cat.toLowerCase()}`}>{cat}</div>
              </div>
              <div className="px-6 pb-1">
                {items.map((s) => (
                  <AccordionItem
                    key={s.id}
                    section={s}
                    isOpen={openId === s.id}
                    onToggle={() => setOpenId(openId === s.id ? null : s.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 card-flat flex items-start gap-3" style={{ background: "var(--surface-3)" }}>
        <Lightbulb size={20} strokeWidth={2.2} className="shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">Astuce</p>
          <p className="text-sm text-[var(--muted)] leading-relaxed">
            Sur chaque page d'événement, le bouton « ? » à droite du stepper relance le mini-tutoriel pas-à-pas qui explique le workflow en 5 cartes.
          </p>
        </div>
      </div>
    </div>
  );
}
